import { useState, useEffect, useCallback } from "react";
import {
	Box,
	TextField,
	Button,
	Grid,
	Typography,
	CircularProgress,
	Alert,
	Card,
	CardMedia,
	CardContent,
	IconButton,
	Tooltip,
	CardActionArea,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Pagination,
	Breadcrumbs,
	Link,
	Divider,
	FormControlLabel,
	Checkbox,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	ListItemButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import FolderIcon from "@mui/icons-material/Folder";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useDropzone } from "react-dropzone";
import { useSnackbar } from "notistack";
import moment from "moment";
import axiosInstance from "../../utils/axios";

const ImageUpload = () => {
	const { enqueueSnackbar } = useSnackbar();

	// State variables
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [images, setImages] = useState([]);
	const [folders, setFolders] = useState([]);
	const [loading, setLoading] = useState(false);
	const [status, setStatus] = useState(null);
	const [previews, setPreviews] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [currentFolderId, setCurrentFolderId] = useState(null);
	const [folderPath, setFolderPath] = useState([{ id: null, name: "Home" }]);
	const itemsPerPage = 8;

	// Dialog states
	const [imagePreviewDialog, setImagePreviewDialog] = useState({
		open: false,
		imageUrl: "",
	});
	const [deleteImageDialog, setDeleteImageDialog] = useState({
		open: false,
		imageId: null,
	});
	const [newFolderDialog, setNewFolderDialog] = useState({
		open: false,
		name: "",
	});
	const [renameFolderDialog, setRenameFolderDialog] = useState({
		open: false,
		folder: null,
		newName: "",
	});
	const [deleteFolderDialog, setDeleteFolderDialog] = useState({
		open: false,
		folder: null,
		deleteContents: false,
	});
	const [moveImageDialog, setMoveImageDialog] = useState({
		open: false,
		image: null,
		selectedFolderId: null,
	});

	// Fetch folders and images when component mounts or current folder changes
	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			setStatus(null);

			try {
				// Fetch folders
				const foldersResponse = await axiosInstance.get("folders");
				if (foldersResponse.status === 200) {
					setFolders(foldersResponse.data);
				}

				// Fetch images for current folder
				const imagesUrl = currentFolderId
					? `images?folderId=${currentFolderId}`
					: "images?folderId=null";

				const imagesResponse = await axiosInstance.get(imagesUrl);
				if (imagesResponse.status === 200) {
					setImages(imagesResponse.data);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
				setStatus({
					type: "error",
					message: "Failed to load data. Please try again.",
				});
				enqueueSnackbar("Failed to load data", { variant: "error" });
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [currentFolderId, enqueueSnackbar]);

	// Clean up preview URLs when component unmounts or files change
	useEffect(() => {
		return () => {
			previews.forEach((url) => URL.revokeObjectURL(url));
		};
	}, [previews]);

	// Dropzone configuration
	const onDrop = useCallback((acceptedFiles) => {
		setSelectedFiles(
			acceptedFiles.map((file) => ({
				file,
				name: file.name,
			}))
		);

		setPreviews(acceptedFiles.map((file) => URL.createObjectURL(file)));
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			"image/*": [],
		},
		multiple: true,
	});

	// Handle folder navigation
	const navigateToFolder = (folderId, folderName) => {
		setCurrentFolderId(folderId);

		if (folderId === null) {
			// Back to home
			setFolderPath([{ id: null, name: "Home" }]);
		} else {
			const folderIndex = folderPath.findIndex((f) => f.id === folderId);

			if (folderIndex === -1) {
				// Add to path
				setFolderPath([...folderPath, { id: folderId, name: folderName }]);
			} else {
				// Truncate path
				setFolderPath(folderPath.slice(0, folderIndex + 1));
			}
		}

		setCurrentPage(1);
	};

	// CRUD operations for folders
	const createFolder = async () => {
		if (!newFolderDialog.name.trim()) {
			enqueueSnackbar("Folder name cannot be empty", { variant: "error" });
			return;
		}

		setLoading(true);
		try {
			const response = await axiosInstance.post("folders", {
				name: newFolderDialog.name,
				parentId: currentFolderId,
			});

			if (response.status === 201) {
				setFolders([...folders, response.data]);
				enqueueSnackbar("Folder created successfully", { variant: "success" });
				setNewFolderDialog({ open: false, name: "" });
			}
		} catch (error) {
			console.error("Error creating folder:", error);
			enqueueSnackbar(
				error.response?.data?.message || "Failed to create folder",
				{ variant: "error" }
			);
		} finally {
			setLoading(false);
		}
	};

	const renameFolder = async () => {
		if (!renameFolderDialog.newName.trim() || !renameFolderDialog.folder) {
			enqueueSnackbar("Folder name cannot be empty", { variant: "error" });
			return;
		}

		setLoading(true);
		try {
			const response = await axiosInstance.put(
				`folders/${renameFolderDialog.folder._id}`,
				{ name: renameFolderDialog.newName }
			);

			if (response.status === 200) {
				// Update folders list
				setFolders(
					folders.map((folder) =>
						folder._id === renameFolderDialog.folder._id
							? { ...folder, name: renameFolderDialog.newName }
							: folder
					)
				);

				// Update breadcrumb path if needed
				setFolderPath(
					folderPath.map((pathItem) =>
						pathItem.id === renameFolderDialog.folder._id
							? { ...pathItem, name: renameFolderDialog.newName }
							: pathItem
					)
				);

				enqueueSnackbar("Folder renamed successfully", { variant: "success" });
				setRenameFolderDialog({ open: false, folder: null, newName: "" });
			}
		} catch (error) {
			console.error("Error renaming folder:", error);
			enqueueSnackbar(
				error.response?.data?.message || "Failed to rename folder",
				{ variant: "error" }
			);
		} finally {
			setLoading(false);
		}
	};

	const deleteFolder = async () => {
		if (!deleteFolderDialog.folder) return;

		setLoading(true);
		try {
			const response = await axiosInstance.delete(
				`folders/${deleteFolderDialog.folder._id}`,
				{
					data: {
						deleteContents: deleteFolderDialog.deleteContents,
					},
				}
			);

			if (response.status === 200) {
				// Remove folder from state
				setFolders(
					folders.filter(
						(folder) => folder._id !== deleteFolderDialog.folder._id
					)
				);

				// Navigate back if we're in the deleted folder
				if (currentFolderId === deleteFolderDialog.folder._id) {
					const parentFolder = folderPath[folderPath.length - 2];
					navigateToFolder(parentFolder.id, parentFolder.name);
				}

				enqueueSnackbar("Folder deleted successfully", { variant: "success" });
				setDeleteFolderDialog({
					open: false,
					folder: null,
					deleteContents: false,
				});
			}
		} catch (error) {
			console.error("Error deleting folder:", error);
			enqueueSnackbar(
				error.response?.data?.message || "Failed to delete folder",
				{ variant: "error" }
			);
		} finally {
			setLoading(false);
		}
	};

	// CRUD operations for images
	const uploadImages = async () => {
		if (selectedFiles.length === 0) return;

		setLoading(true);
		setStatus(null);

		const uploadPromises = selectedFiles.map(async ({ file, name }) => {
			try {
				const formData = new FormData();
				formData.append("file", file);
				formData.append("name", name);

				if (currentFolderId) {
					formData.append("folderId", currentFolderId);
				}

				const response = await axiosInstance.post("images/upload", formData, {
					headers: { "Content-Type": "multipart/form-data" },
				});

				return response.data;
			} catch (error) {
				console.error("Error uploading file:", file.name, error);
				throw error;
			}
		});

		try {
			const results = await Promise.allSettled(uploadPromises);

			const successful = results.filter(
				(result) => result.status === "fulfilled"
			);
			const failed = results.filter((result) => result.status === "rejected");

			// Add successful uploads to the images state
			const newImages = successful.map((result) => result.value);
			setImages((prevImages) => [...prevImages, ...newImages]);

			// Set appropriate status message
			if (failed.length === 0) {
				setStatus({
					type: "success",
					message: `Successfully uploaded ${successful.length} image${
						successful.length !== 1 ? "s" : ""
					}`,
				});
			} else if (successful.length === 0) {
				setStatus({
					type: "error",
					message: "Failed to upload images. Please try again.",
				});
			} else {
				setStatus({
					type: "warning",
					message: `Uploaded ${successful.length} image${
						successful.length !== 1 ? "s" : ""
					}, but ${failed.length} failed`,
				});
			}

			// Clear selected files after upload
			setSelectedFiles([]);
			setPreviews([]);
		} catch (error) {
			console.error("Error in image upload:", error);
			setStatus({
				type: "error",
				message: "Error uploading files. Please try again.",
			});
		} finally {
			setLoading(false);
		}
	};

	const deleteImage = async () => {
		if (!deleteImageDialog.imageId) return;

		setLoading(true);
		try {
			const response = await axiosInstance.delete(
				`/images/${deleteImageDialog.imageId}`
			);

			if (response.status === 200) {
				setImages((prevImages) =>
					prevImages.filter((img) => img._id !== deleteImageDialog.imageId)
				);
				enqueueSnackbar("Image deleted successfully", { variant: "success" });
			} else {
				enqueueSnackbar("Failed to delete the image", { variant: "error" });
			}
		} catch (error) {
			console.error("Error deleting image:", error);
			enqueueSnackbar("Error deleting image", { variant: "error" });
		} finally {
			setDeleteImageDialog({ open: false, imageId: null });
			setLoading(false);
		}
	};

	const moveImage = async () => {
		const { image, selectedFolderId } = moveImageDialog;
		if (!image) return;

		setLoading(true);
		try {
			const response = await axiosInstance.put(`images/${image._id}/move`, {
				folderId: selectedFolderId, // null is valid for root
			});

			if (response.status === 200) {
				// Remove the image from current display if it was moved to a different folder
				if (selectedFolderId !== currentFolderId) {
					setImages(images.filter((img) => img._id !== image._id));
				}

				enqueueSnackbar("Image moved successfully", { variant: "success" });
				setMoveImageDialog({
					open: false,
					image: null,
					selectedFolderId: null,
				});
			}
		} catch (error) {
			console.error("Error moving image:", error);
			enqueueSnackbar(error.response?.data?.message || "Failed to move image", {
				variant: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	// Helper functions
	const handleNameChange = (name, index) => {
		const newFiles = [...selectedFiles];
		newFiles[index].name = name;
		setSelectedFiles(newFiles);
	};

	const copyToClipboard = (text) => {
		navigator.clipboard
			.writeText(text)
			.then(() => {
				enqueueSnackbar("URL copied to clipboard", { variant: "success" });
			})
			.catch((err) => {
				console.error("Failed to copy URL: ", err);
				enqueueSnackbar("Failed to copy URL", { variant: "error" });
			});
	};

	const handlePageChange = (event, value) => {
		setCurrentPage(value);
	};

	// Filter current folder's subfolders
	const currentSubfolders = folders.filter(
		(folder) => folder.parentId === currentFolderId
	);

	// Filter and paginate images
	const filteredImages = images
		.filter((image) =>
			image.imageName.toLowerCase().includes(searchTerm.toLowerCase())
		)
		.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));

	const totalPages = Math.ceil(filteredImages.length / itemsPerPage);
	const displayedImages = filteredImages.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage
	);

	// Get available folders for move dialog (excluding current folder and descendants)
	const getAvailableFolders = () => {
		// Helper function to get all descendant folder IDs
		const getDescendantIds = (folderId) => {
			if (!folderId) return []; // Handle null/undefined folderId

			// Find direct children of this folder
			const directChildren = folders
				.filter((f) => f.parentId === folderId)
				.map((f) => f._id);

			// Start with direct children
			let descendants = [...directChildren];

			// Then recursively add their children
			directChildren.forEach((childId) => {
				descendants = [...descendants, ...getDescendantIds(childId)];
			});

			return descendants;
		};

		// Get current folder ID and its descendants to exclude
		const currentFolderId = moveImageDialog.image?.folderId;
		const excludeIds = currentFolderId
			? [currentFolderId, ...getDescendantIds(currentFolderId)]
			: [];

		// Debug logs (can be removed after fixing)
		console.log("Current folder ID:", currentFolderId);
		console.log("Folders to exclude:", excludeIds);
		console.log(
			"All available folders:",
			folders.map((f) => ({ id: f._id, name: f.name, parentId: f.parentId }))
		);

		// Filter available folders
		return folders.filter((folder) => !excludeIds.includes(folder._id));
	};

	return (
		<Box sx={{ p: 2, bgcolor: "background.paper" }}>
			{/* Breadcrumb navigation */}
			<Breadcrumbs
				separator={<NavigateNextIcon fontSize="small" />}
				aria-label="folder navigation"
				sx={{ mb: 2 }}
			>
				{folderPath.map((folder, index) => (
					<Link
						key={index}
						color="inherit"
						component="button"
						onClick={() => navigateToFolder(folder.id, folder.name)}
						sx={{ display: "flex", alignItems: "center" }}
					>
						{index === 0 ? (
							<HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
						) : (
							<FolderIcon sx={{ mr: 0.5 }} fontSize="small" />
						)}
						{folder.name}
					</Link>
				))}
			</Breadcrumbs>

			{/* Upload Section */}
			<Typography variant="h5" gutterBottom>
				Upload Images
			</Typography>
			<Box
				sx={{
					width: "100%",
					height: "200px",
					border: "2px dashed",
					borderColor: isDragActive ? "primary.main" : "grey.400",
					borderRadius: 2,
					p: 2,
					mb: 2,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					transition: "border-color 0.2s ease-in-out",
					bgcolor: isDragActive ? "action.hover" : "background.paper",
				}}
				{...getRootProps()}
			>
				<input {...getInputProps()} />
				<CloudUploadIcon
					color={isDragActive ? "primary" : "action"}
					style={{ fontSize: 72 }}
				/>
				<Typography
					variant="body1"
					color={isDragActive ? "primary" : "textSecondary"}
					align="center"
					sx={{ mt: 2 }}
				>
					{isDragActive
						? "Drop the images here..."
						: "Drag 'n' drop images here, or click to select images"}
				</Typography>
			</Box>

			{/* Preview & Name Section */}
			{previews.length > 0 && (
				<>
					<Typography variant="h6" gutterBottom>
						Selected Images ({previews.length})
					</Typography>
					<Grid container spacing={2} sx={{ mb: 2 }}>
						{previews.map((preview, index) => (
							<Grid item xs={12} sm={6} md={4} lg={3} key={index}>
								<Card sx={{ height: "100%" }}>
									<Box sx={{ position: "relative", pt: "56.25%" }}>
										{" "}
										{/* 16:9 Aspect Ratio */}
										<CardMedia
											component="img"
											image={preview}
											alt={`Preview ${index + 1}`}
											sx={{
												position: "absolute",
												top: 0,
												left: 0,
												width: "100%",
												height: "100%",
												objectFit: "cover",
											}}
										/>
									</Box>
									<CardContent>
										<TextField
											fullWidth
											label="Image Name"
											type="text"
											size="small"
											value={selectedFiles[index].name}
											onChange={(e) => handleNameChange(e.target.value, index)}
											disabled={loading}
										/>
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>
					<Button
						variant="contained"
						color="primary"
						onClick={uploadImages}
						startIcon={<CloudUploadIcon />}
						disabled={loading}
						sx={{ mb: 3 }}
					>
						Upload {selectedFiles.length} Image
						{selectedFiles.length !== 1 ? "s" : ""}
					</Button>
				</>
			)}

			{/* Status Alerts */}
			{status && (
				<Alert severity={status.type} sx={{ mb: 2 }}>
					{status.message}
				</Alert>
			)}

			{/* Loading Indicator */}
			{loading && (
				<Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
					<CircularProgress />
				</Box>
			)}

			{/* Folder Management Section */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mt: 2,
				}}
			>
				<Typography variant="h5">
					{currentFolderId
						? `Folder: ${folderPath[folderPath.length - 1].name}`
						: "All Images"}
				</Typography>
				<Button
					variant="outlined"
					startIcon={<CreateNewFolderIcon />}
					onClick={() => setNewFolderDialog({ ...newFolderDialog, open: true })}
				>
					New Folder
				</Button>
			</Box>

			{/* Search Bar */}
			<TextField
				fullWidth
				margin="normal"
				variant="outlined"
				label="Search Images"
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				placeholder="Search by name..."
				sx={{ mb: 2 }}
			/>

			{/* Subfolders Grid */}
			{currentSubfolders.length > 0 && (
				<>
					<Typography variant="h6" gutterBottom>
						Folders
					</Typography>
					<Grid container spacing={2}>
						{currentSubfolders.map((folder) => (
							<Grid item xs={6} sm={4} md={3} lg={2} key={folder._id}>
								<Card
									sx={{
										height: "100%",
										transition: "transform 0.2s, box-shadow 0.2s",
										"&:hover": {
											transform: "translateY(-4px)",
											boxShadow: 4,
										},
									}}
								>
									<CardActionArea
										onClick={() => navigateToFolder(folder._id, folder.name)}
										sx={{ height: "100%", p: 2, position: "relative" }}
									>
										<Box
											sx={{
												position: "absolute",
												top: 8,
												right: 8,
												display: "flex",
												opacity: 0,
												transition: "0.2s",
												".MuiCard-root:hover &": {
													opacity: 1,
												},
												zIndex: 2,
												bgcolor: "rgba(255,255,255,0.8)",
												borderRadius: 1,
												p: 0.5,
											}}
											onClick={(e) => e.stopPropagation()}
										>
											<Tooltip title="Rename folder">
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														setRenameFolderDialog({
															open: true,
															folder: folder,
															newName: folder.name,
														});
													}}
												>
													<EditIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Delete folder">
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														setDeleteFolderDialog({
															open: true,
															folder: folder,
															deleteContents: false,
														});
													}}
												>
													<DeleteForeverIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</Box>
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
											}}
										>
											<FolderIcon
												sx={{ fontSize: 64, color: "#f8d775", mb: 1 }}
											/>
											<Typography
												variant="subtitle1"
												align="center"
												sx={{ wordBreak: "break-word" }}
											>
												{folder.name}
											</Typography>
										</Box>
									</CardActionArea>
								</Card>
							</Grid>
						))}
					</Grid>
					<Divider sx={{ my: 3 }} />
				</>
			)}

			{/* Images Grid */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Typography variant="h6">
					Images {filteredImages.length > 0 ? `(${filteredImages.length})` : ""}
				</Typography>
				{totalPages > 1 && (
					<Pagination
						count={totalPages}
						page={currentPage}
						onChange={handlePageChange}
						color="primary"
						size="small"
					/>
				)}
			</Box>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress />
				</Box>
			) : displayedImages.length > 0 ? (
				<>
					<Grid container spacing={2}>
						{displayedImages.map((image) => (
							<Grid item xs={12} sm={6} md={4} lg={3} key={image._id}>
								<Card
									sx={{
										height: "100%",
										transition: "transform 0.2s, box-shadow 0.2s",
										"&:hover": {
											transform: "translateY(-4px)",
											boxShadow: 4,
										},
									}}
								>
									<CardMedia
										component="div"
										image={image.imageUrl}
										sx={{
											pt: "56.25%", // 16:9 aspect ratio
											position: "relative",
											backgroundImage: `${image.imageUrl}`,
											backgroundSize: "cover",
											backgroundPosition: "center",
										}}
									>
										<Box
											sx={{
												position: "absolute",
												top: 0,
												left: 0,
												width: "100%",
												height: "100%",
												bgcolor: "rgba(0,0,0,0.4)",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												opacity: 0,
												transition: "0.3s",
												"&:hover": {
													opacity: 1,
												},
											}}
										>
											<Tooltip title="View fullscreen">
												<IconButton
													onClick={() =>
														setImagePreviewDialog({
															open: true,
															imageUrl: image.imageUrl,
														})
													}
													sx={{ color: "white", mx: 0.5 }}
												>
													<FullscreenIcon />
												</IconButton>
											</Tooltip>
											<Tooltip title="Copy URL">
												<IconButton
													onClick={() => copyToClipboard(image.imageUrl)}
													sx={{ color: "white", mx: 0.5 }}
												>
													<FileCopyIcon />
												</IconButton>
											</Tooltip>
											<Tooltip title="Move to folder">
												<IconButton
													onClick={() =>
														setMoveImageDialog({
															open: true,
															image: image,
															selectedFolderId: null,
														})
													}
													sx={{ color: "white", mx: 0.5 }}
												>
													<FolderIcon />
												</IconButton>
											</Tooltip>
											<Tooltip title="Delete image">
												<IconButton
													onClick={() =>
														setDeleteImageDialog({
															open: true,
															imageId: image._id,
														})
													}
													sx={{ color: "white", mx: 0.5 }}
												>
													<DeleteForeverIcon />
												</IconButton>
											</Tooltip>
										</Box>
									</CardMedia>
									<CardContent>
										<Typography variant="h6" gutterBottom noWrap>
											{image.name || image.imageName}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Uploaded: {moment(image.uploadedAt).format("MMM Do YYYY")}
										</Typography>
										<Typography variant="body2" color="text.secondary" noWrap>
											Filename: {image.imageName}
										</Typography>
										{image.uploadedBy && (
											<Typography variant="body2" color="text.secondary" noWrap>
												By: {image.uploadedBy}
											</Typography>
										)}
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>

					{/* Bottom Pagination */}
					{totalPages > 1 && (
						<Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
							<Pagination
								count={totalPages}
								page={currentPage}
								onChange={handlePageChange}
								color="primary"
							/>
						</Box>
					)}
				</>
			) : (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						py: 6,
						bgcolor: "action.hover",
						borderRadius: 2,
					}}
				>
					<InsertDriveFileIcon
						sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
					/>
					<Typography variant="h6" color="text.secondary" gutterBottom>
						No images found
					</Typography>
					<Typography variant="body2" color="text.secondary" align="center">
						{searchTerm
							? "Try a different search term"
							: "Upload some images to get started"}
					</Typography>
				</Box>
			)}

			{/* DIALOGS */}

			{/* Image Preview Dialog */}
			<Dialog
				open={imagePreviewDialog.open}
				onClose={() =>
					setImagePreviewDialog({ ...imagePreviewDialog, open: false })
				}
				maxWidth="lg"
				fullWidth
			>
				<DialogContent sx={{ p: 1 }}>
					<img
						src={imagePreviewDialog.imageUrl}
						alt="Preview"
						style={{ width: "100%", height: "auto" }}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => copyToClipboard(imagePreviewDialog.imageUrl)}
						startIcon={<FileCopyIcon />}
					>
						Copy URL
					</Button>
					<Button
						onClick={() => setImagePreviewDialog({ open: false, imageUrl: "" })}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Image Confirmation Dialog */}
			<Dialog
				open={deleteImageDialog.open}
				onClose={() => setDeleteImageDialog({ open: false, imageId: null })}
			>
				<DialogTitle>Delete Image</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete this image? This action cannot be
						undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeleteImageDialog({ open: false, imageId: null })}
					>
						Cancel
					</Button>
					<Button onClick={deleteImage} color="error">
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* New Folder Dialog */}
			<Dialog
				open={newFolderDialog.open}
				onClose={() => setNewFolderDialog({ open: false, name: "" })}
			>
				<DialogTitle>Create New Folder</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Enter a name for your new folder
						{currentFolderId
							? ` inside ${folderPath[folderPath.length - 1].name}`
							: ""}
						:
					</DialogContentText>
					<TextField
						autoFocus
						margin="dense"
						label="Folder Name"
						type="text"
						fullWidth
						value={newFolderDialog.name}
						onChange={(e) =>
							setNewFolderDialog({ ...newFolderDialog, name: e.target.value })
						}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setNewFolderDialog({ open: false, name: "" })}>
						Cancel
					</Button>
					<Button
						onClick={createFolder}
						color="primary"
						disabled={!newFolderDialog.name.trim() || loading}
					>
						Create
					</Button>
				</DialogActions>
			</Dialog>

			{/* Rename Folder Dialog */}
			<Dialog
				open={renameFolderDialog.open}
				onClose={() =>
					setRenameFolderDialog({ open: false, folder: null, newName: "" })
				}
			>
				<DialogTitle>Rename Folder</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Enter a new name for the folder "{renameFolderDialog.folder?.name}":
					</DialogContentText>
					<TextField
						autoFocus
						margin="dense"
						label="New Folder Name"
						type="text"
						fullWidth
						value={renameFolderDialog.newName}
						onChange={(e) =>
							setRenameFolderDialog({
								...renameFolderDialog,
								newName: e.target.value,
							})
						}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() =>
							setRenameFolderDialog({ open: false, folder: null, newName: "" })
						}
					>
						Cancel
					</Button>
					<Button
						onClick={renameFolder}
						color="primary"
						disabled={!renameFolderDialog.newName.trim() || loading}
					>
						Rename
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Folder Dialog */}
			<Dialog
				open={deleteFolderDialog.open}
				onClose={() =>
					setDeleteFolderDialog({
						open: false,
						folder: null,
						deleteContents: false,
					})
				}
			>
				<DialogTitle>Delete Folder</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete the folder "
						{deleteFolderDialog.folder?.name}"? This action cannot be undone.
					</DialogContentText>
					<FormControlLabel
						control={
							<Checkbox
								checked={deleteFolderDialog.deleteContents}
								onChange={(e) =>
									setDeleteFolderDialog({
										...deleteFolderDialog,
										deleteContents: e.target.checked,
									})
								}
								color="primary"
							/>
						}
						label="Also delete all contents inside this folder"
						sx={{ mt: 2 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() =>
							setDeleteFolderDialog({
								open: false,
								folder: null,
								deleteContents: false,
							})
						}
					>
						Cancel
					</Button>
					<Button onClick={deleteFolder} color="error" disabled={loading}>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* Move Image Dialog */}
			<Dialog
				open={moveImageDialog.open}
				onClose={() =>
					setMoveImageDialog({
						open: false,
						image: null,
						selectedFolderId: null,
					})
				}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Move Image</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Select a destination folder for "{moveImageDialog.image?.imageName}
						":
					</DialogContentText>

					<List sx={{ mt: 2 }}>
						{/* Root folder option */}
						<ListItem
							disablePadding
							selected={moveImageDialog.selectedFolderId === null}
						>
							<ListItemButton
								onClick={() =>
									setMoveImageDialog({
										...moveImageDialog,
										selectedFolderId: null,
									})
								}
							>
								<ListItemIcon>
									<HomeIcon
										color={
											moveImageDialog.selectedFolderId === null
												? "primary"
												: "inherit"
										}
									/>
								</ListItemIcon>
								<ListItemText primary="Home (Root)" />
							</ListItemButton>
						</ListItem>

						{/* Available folders */}
						{moveImageDialog.open &&
							getAvailableFolders().map((folder) => {
								// Calculate indentation level (for nested folders)
								const getIndentationLevel = (folderId) => {
									let level = 0;
									let parent = folders.find(
										(f) => f._id === folderId
									)?.parentId;

									while (parent) {
										level++;
										parent = folders.find((f) => f._id === parent)?.parentId;
									}

									return level;
								};

								const indentLevel = getIndentationLevel(folder._id);

								return (
									<ListItem
										key={folder._id}
										disablePadding
										selected={moveImageDialog.selectedFolderId === folder._id}
									>
										<ListItemButton
											onClick={() =>
												setMoveImageDialog({
													...moveImageDialog,
													selectedFolderId: folder._id,
												})
											}
											sx={{ pl: 2 + indentLevel * 2 }}
										>
											<ListItemIcon>
												<FolderIcon
													color={
														moveImageDialog.selectedFolderId === folder._id
															? "primary"
															: "inherit"
													}
													sx={{
														color:
															moveImageDialog.selectedFolderId === folder._id
																? undefined
																: "#f8d775",
													}}
												/>
											</ListItemIcon>
											<ListItemText primary={folder.name} />
										</ListItemButton>
									</ListItem>
								);
							})}
					</List>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() =>
							setMoveImageDialog({
								open: false,
								image: null,
								selectedFolderId: null,
							})
						}
					>
						Cancel
					</Button>
					<Button onClick={moveImage} color="primary" disabled={loading}>
						Move
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ImageUpload;
